import { Category } from '@wix/answers-api';
import { pageClick, clickOnAnswer } from '@wix/bi-logger-new-help-center/v2';
import { useCallback } from 'react';
import { BI } from '../../constants';
import { ArticleSearchResultItem } from '../../types';
import { mapArticleTypeToItemType } from '../../utils/bi';
import { useBI } from '../../hooks/useBI';

export const useSearchResultsBi = (term: string, locale: string, searchSessionId: string) => {
    const { sendBIEvent } = useBI();

    const common = {
        source_name: BI.SOURCE_NAMES.SEARCH_RESULTS,
        kb_lang: locale,
    };

    const onArticleTopicBadgeClick = useCallback((
        topic: Category,
        itemOrder: number
    ) => {
        void sendBIEvent(
            pageClick({
                ...common,
                clicked_item_type: BI.CLICKED_ITEM_TYPES.CATEGORY_SELECTION,
                clicked_url: topic.url,
                clicked_text: topic.name,
                clicked_item_order: `${itemOrder}`,
                clicked_item_id: topic.id,
            })
        );
    }, [locale]);

    const onArticleResolutionBadgeClick = useCallback((
        clickedText: string,
        url: string,
        itemOrder: number
    ) => {
        void sendBIEvent(
            pageClick({
                ...common,
                clicked_item_type: BI.CLICKED_ITEM_TYPES.TAG_SELECTION,
                clicked_text: clickedText,
                clicked_url: url,
                clicked_item_order: `${itemOrder}`,
            })
        );
    }, [locale]);

    const onArticleTypeTagClick = useCallback((
        clickedText: string,
        url: string,
        itemOrder: number
    ) => {
        void sendBIEvent(
            pageClick({
                ...common,
                clicked_item_type: BI.CLICKED_ITEM_TYPES.SOURCE_HOMEPAGE_SELECTION,
                clicked_text: clickedText,
                clicked_url: url,
                clicked_item_order: `${itemOrder}`,
            })
        );
    }, [locale]);

    const onArticleClick = useCallback((
        article: ArticleSearchResultItem,
        itemOrder: number,
        currentPage: number,
        searchMethod?: string,
    ) => {
        void sendBIEvent(
            clickOnAnswer({
                source_name: BI.SOURCE_NAMES.SEARCH_RESULTS,
                kb_lang: locale as string,
                clicked_url: article.url,
                clicked_item_id: article.id,
                clicked_item_order: `${itemOrder}`,
                question: term,
                item_type: mapArticleTypeToItemType(article.type, article.docType),
                clicked_item_type: mapArticleTypeToItemType(article.type, article.docType),
                search_result_selection:
                    BI.SEARCH_RESULT_SELECTION.FULL_SEARCH_RESULTS_LIST,
                page_number: currentPage,
                search_method: searchMethod,
                ...(article?.distance
                    ? { output_distance_str: `${article.distance}` }
                    : {}),
                search_session_Id: searchSessionId,
            })
        );
    }, [locale, searchSessionId, sendBIEvent, term]);

    const onPageChange = useCallback((page: number) => {
        void sendBIEvent(
            pageClick({
                source_name: BI.SOURCE_NAMES.SEARCH_RESULTS,
                kb_lang: locale as string,
                clicked_item_type: BI.CLICKED_ITEM_TYPES.PAGE_SELECTION,
                clicked_url: `${window.location.origin}/search&page=${page}`,
                clicked_text: `${page}`,
            })
        );
    }, [locale]);

    return {
        onArticleTopicBadgeClick,
        onArticleResolutionBadgeClick,
        onArticleTypeTagClick,
        onArticleClick,
        onPageChange,
    };
};
